import React, { useContext, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { FaAngleDown } from "react-icons/fa6";
import Logout from "./Logout.js";
import { HOME } from "../routes.js";
import { Viya_Electric_Logo } from "../Assets/index.js";
import { menuItemsData } from "../Constants/headerItems.js";
import { UserContext } from "../Context/UserContext.js";
import NotificationBell from "../Pages/Notification/NotificationBell.js";
import { formatRole } from "../Utils/formatRole.js";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const { user } = useContext(UserContext);

  const userRole = user?.role;
  const userFirstName = user?.full_name.split(" ")[0] || "";

  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const toggleMenu = () => {
    setIsMenuOpen((isMenuOpen) => !isMenuOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
        setIsSubMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <nav className="flex justify-between bg-primary border-gray-200 p-4">
      <div>
        <div className="container flex items-center">
          {/* Hamburger (always visible, extreme left) */}
          <button
            ref={buttonRef}
            type="button"
            className="bg-secondary inline-flex items-center justify-center w-10 h-10 mr-4 text-primary rounded-lg hover:bg-gray-100"
            aria-controls="dropdown-menu"
            aria-expanded={isMenuOpen}
            onClick={toggleMenu}
          >
            <span className="sr-only">Open main menu</span>
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              {isMenuOpen ? (
                // X (close)
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                // Hamburger
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 6h18M3 12h18M3 18h18"
                />
              )}
            </svg>
          </button>

          {/* Logo */}
          <a href={HOME} className="flex items-center space-x-2">
            <img
              src={Viya_Electric_Logo}
              alt="Viya Logo"
              className="h-8 w-12 md:h-10 md:w-15"
            />
            <span className="self-center text-xl font-semibold whitespace-nowrap text-secondary">
              {process.env.REACT_APP_TITLE}
            </span>
          </a>
        </div>
      </div>

      <div className="flex justify-end gap-4">
        <div className="flex items-center gap-4">
          <NotificationBell />
        </div>

        <div className="group relative flex items-center bg-white text-primary font-semibold px-4 py-1 shadow-md hover:shadow-lg transition-shadow rounded-md">
          <span>Hi! {userFirstName}</span>

          {userRole && (
            <span
              className="absolute left-1/2 -translate-x-1/2 top-full mt-2
                 px-2 py-1 text-xs text-white bg-gray-700 rounded
                 opacity-0 group-hover:opacity-100
                 transition-opacity duration-200
                 whitespace-nowrap z-50"
            >
              {formatRole(userRole)}
            </span>
          )}
        </div>

        <button
            onClick={() => Logout()}
            className="hidden md:flex items-center justify-center text-red-600"
            title="Logout"
          >
            <Logout isMenuOpen={false} />
          </button>
      </div>

      {/* Dropdown Menu (below hamburger) */}
      {isMenuOpen && (
        <div
          ref={dropdownRef}
          id="dropdown-menu"
          className="absolute top-full left-0 w-1/2 md:w-1/3 bg-white text-white transition-all duration-300 ease-in-out shadow-xl z-50 max-h-[70vh] overflow-y-auto"
        >
          <ul className="flex flex-col py-2 px-4 font-medium relative">
            {menuItemsData
              .filter((item) => item.roles.includes(userRole))
              .map((item, idx) => (
                <li key={idx}>
                  {item.subItems ? (
                    <div>
                      <span
                        onClick={() =>
                          setIsSubMenuOpen(
                            isSubMenuOpen === item.name ? null : item.name
                          )
                        }
                        className="flex items-center py-2 px-4 cursor-pointer text-primary bg-white border-b border-gray-100 last:border-b-0 rounded-md"
                      >
                        {item.name}
                        <span className="flex-col justify-start mt-1 pl-1">
                          <FaAngleDown
                            className={`transform transition-transform duration-200 ${
                              isSubMenuOpen === item.name ? "rotate-180" : ""
                            }`}
                          />
                        </span>
                      </span>

                      {/* Show sub menu if opened */}
                      {isSubMenuOpen === item.name && (
                        <ul className="pl-6">
                          {item.subItems
                          .map((subItem, subIdx) => (
                            <li key={subIdx}>
                              {subItem.route && (
                                <Link
                                  to={subItem.route}
                                  onClick={() => {
                                    setIsSubMenuOpen(false);
                                    setIsMenuOpen(false);
                                  }}
                                  className="block py-2 px-4 text-primary bg-white border-b border-gray-100 last:border-b-0 rounded-md hover:text-secondary"
                                >
                                  {subItem.name}
                                </Link>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : (
                    item.route && (
                      <Link
                        to={item.route}
                        onClick={() => setIsMenuOpen(false)}
                        className="block py-2 px-4 text-primary bg-white border-b border-gray-100 rounded-md hover:text-secondary"
                      >
                        {item.name}
                      </Link>
                    )
                  )}
                </li>
              ))}
            <li>
              <Logout isMenuOpen={isMenuOpen} />
            </li>
          </ul>
        </div>
      )}
    </nav>
  );
};

export default Header;
